import { TIME_LAPSE } from '../../../baseBtl/catalog/products/Enum';

export class DateUniversalBase {
    public value: Date;
    private weekDays: string[] = ['dimanche', 'lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi'];

    constructor(date?: string) {
        const FORMAT = {
            US_SIMPLE: /^\d{4}-[0-1]\d-[0-3]\d$/,
            US_COMPLEX: /^\d{4}-[0-1]\d-[0-3]\d [0-2]\d:[0-5]\d:[0-5]\d$/,
            GB_SIMPLE: /^\d{4}-[0-1]\d-[0-3]\d$/,
            FR_SIMPLE: /^[0-3]\d\/[0-1]\d\/\d{4}$/,
            NOW: /now/,
        };
        switch (true) {
            case FORMAT.NOW.test(date):
                this.value = new Date();
                break;
            case FORMAT.US_SIMPLE.test(date):
                this.value = new Date(date.replace(/-/g, '/'));
                break;
            case FORMAT.US_COMPLEX.test(date):
                this.value = new Date(date.replace(/-/g, '/'));
                break;
            case FORMAT.FR_SIMPLE.test(date):
                const dates = date.split('/');
                this.value = new Date(`${dates[2]}/${dates[1]}/${dates[0]}`);
                break;
            default:
                this.value = new Date(date);
        }
    }

    /**
     * Check if dateIn is between dateA and dateB
     * @param dateA
     * @param dateB
     * @param dateIn
     * @param strict
     * @returns
     */
    public static dateBetween(
        dateA: DateUniversalBase,
        dateB: DateUniversalBase,
        dateIn: DateUniversalBase,
        strict: boolean = true,
    ): boolean {
        if (!strict) {
            return dateIn.timestamp() >= dateA.timestamp() && dateB.timestamp() >= dateIn.timestamp();
        }
        return dateIn.timestamp() > dateA.timestamp() && dateB.timestamp() > dateIn.timestamp();
    }

    /**
     * @function addDay
     * @description Add x Month to date
     * @param num
     * @returns
     */
    public addDay(num: number): DateUniversalBase {
        this.value.setDate(this.value.getDate() + num);
        return this;
    }

    /**
     * @function addMonth
     * @description Add x Month to date
     * @param num
     * @returns
     */
    public addMonth(num: number): DateUniversalBase {
        this.value.setMonth(this.value.getMonth() + num);
        return this;
    }

    /**
     * @function removeMonth
     * @description Remove x Month to date
     * @param num
     * @returns
     */
    public removeMonth(num: number): DateUniversalBase {
        this.value.setMonth(this.value.getMonth() - num);
        return this;
    }

    /**
     * @function addYear
     * @description Add x Year to date
     * @param num
     * @returns
     */
    public addYear(num: number): DateUniversalBase {
        this.value.setFullYear(this.value.getFullYear() + num);
        return this;
    }

    /**
     * @function removeYear
     * @description Remove x Year to date
     * @param num
     * @returns
     */
    public removeYear(num: number): DateUniversalBase {
        this.value.setFullYear(this.value.getFullYear() - num);
        return this;
    }

    /**
     * @function timestamp
     * @description return php timestamp.
     * @return
     */
    public timestamp(): number {
        return this.value.getTime() / 1000;
    }

    /**
     * @function toSimpleFrench
     * @description return date with format dd/mm/yyyy.
     * @return
     */
    public toSimpleFrench(): string {
        const month: string | number =
            this.value.getMonth() + 1 < 10 ? '0' + (this.value.getMonth() + 1) : this.value.getMonth() + 1;
        const day: string | number = this.value.getDate() < 10 ? '0' + this.value.getDate() : this.value.getDate();
        return `${day}/${month}/${this.value.getFullYear()}`;
    }

    /**
     * @function toShortSimpleFrench
     * @description return date with format dd/mm.
     * @return
     */
    public toShortSimpleFrench(): string {
        const month: string = ('0' + (this.value.getMonth() + 1)).slice(-2);
        const day: string = ('0' + this.value.getDate()).slice(-2);
        return `${day}/${month}`;
    }

    /**
     * @function toYYYYMMJJ
     * @description return date with format YYYY-MM-JJ.
     * @return
     */
    public toYYYYMMJJ(): string {
        const month: string | number =
            this.value.getMonth() + 1 < 10 ? '0' + (this.value.getMonth() + 1) : this.value.getMonth() + 1;
        const day: string | number = this.value.getDate() < 10 ? '0' + this.value.getDate() : this.value.getDate();
        return `${this.value.getFullYear()}-${month}-${day}`;
    }

    /**
     * @function daysOrHoursAgo
     * @description Returns days or hours past since {time}
     * Explanation : 36e5 ms = 60 m x 60 s x 1000 ms = 3600000 ms
     * @param string
     * @returns
     */
    public daysOrHoursAgo(time: string): string {
        const pastDate: Date = new Date(time);

        const hours = Math.floor(Math.abs(this.value.getTime() - pastDate.getTime()) / 36e5);

        if (hours > 24) {
            return Math.floor(hours / 24) + ' jours';
        }

        return hours + ' heures';
    }

    /**
     * @function addMinutes
     * @description Returns date object with added minutes
     * Explanation : 60 s x 1000 ms === 60000 ms === 6e4 ms
     * @param num
     * @returns
     */
    public addMinutes(num: number): DateUniversalBase {
        this.value = new Date(this.value.getTime() + num * 6e4);
        return this;
    }

    /**
     * @function getTime
     * @description Returns Date.getTime() from this.value
     * @returns
     */
    public getTime(): number {
        return this.value.getTime();
    }

    /**
     * @function getWeekDay
     * @description Returns week of the day wording
     * @returns
     */
    public getWeekDay(): string {
        return this.weekDays[this.value.getDay()];
    }

    /**
     * @function getTimeLapse
     * @description Return a time lapse in the defined enum
     * @return
     */
    public getTimeLapse() {
        const now = new Date();
        const time = this.value.getTime() - now.getTime();

        switch (true) {
            case time < 86400000:
                return TIME_LAPSE.DAY;
            case time < 6 * 86400000:
                return TIME_LAPSE.WEEK;
            default:
                return TIME_LAPSE.FUTURE;
        }
    }
}
